import { PaletteMode, ThemeOptions } from '@mui/material/styles';

export const createComponents = ({
  mode,
}: {
  mode: PaletteMode;
}): Pick<ThemeOptions, 'components'> => ({
  components: {
    MuiCircularProgress: {
      defaultProps: {
        color: 'inherit',
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '8px',
          '&.small': {
            '& span': {
              height: '16px',
              width: '16px',
              '&::before': {
                backgroundSize: '10px',
              },
            },
          },
          '&.medium': {
            '& span': {
              height: '20px',
              width: '20px',
              '&::before': {
                backgroundSize: '14px',
              },
            },
          },
          '&.large': {
            '& span': {
              height: '24px',
              width: '24px',
              '&::before': {
                backgroundSize: '14px',
              },
            },
          },
        },
      },
    },

    MuiButton: {
      defaultProps: {
        size: 'medium',
        color: 'primary',
        variant: 'contained',
      },

      variants: [
        // GROUP: CONTAINED
        {
          props: { variant: 'contained', color: 'primary' },
          style: ({ theme }) => ({
            '&.MuiButton-root': {
              color: theme.palette['button-white'],
              backgroundColor: theme.palette['button-blue-100'],
              '&:hover': {
                backgroundColor: theme.palette['button-blue-200'],
              },
            },
          }),
        },

        {
          props: { variant: 'contained', color: 'secondary' },
          style: ({ theme }) => ({
            '&.MuiButton-root': {
              color: theme.palette['button-white'],
              backgroundColor: theme.palette['button-green-100'],
              '&:hover': {
                backgroundColor: theme.palette['button-green-200'],
              },
            },
          }),
        },

        {
          props: { variant: 'contained', color: 'error' },
          style: ({ theme }) => ({
            '&.MuiButton-root': {
              color: theme.palette['button-white'],
              backgroundColor: theme.palette['button-red-100'],
              '&:hover': {
                backgroundColor: theme.palette['button-red-200'],
              },
            },
          }),
        },

        {
          props: { variant: 'contained', color: 'neutral' },
          style: ({ theme }) => ({
            '&.MuiButton-root': {
              color: theme.palette['button-blue-100'],
              backgroundColor: theme.palette['button-white'],
              '&:hover': {
                boxShadow: theme.shadows[2],
              },
            },
          }),
        },

        {
          props: { variant: 'contained', disabled: true },
          style: ({ theme }) => ({
            '&.Mui-disabled': {
              color: theme.palette['button-grey-300'],
              backgroundColor: theme.palette['button-grey-150'],
            },
          }),
        },

        {
          props: { variant: 'contained', disabled: true, loading: true },
          style: ({ theme }) => ({
            '&.Mui-disabled': {
              color: theme.palette['button-white'],
              backgroundColor: theme.palette['button-grey-150'],
            },
          }),
        },

        // GROUP: OUTLINED
        {
          props: { variant: 'outlined', color: 'primary' },
          style: ({ theme }) => ({
            '&.MuiButton-root': {
              color: theme.palette['button-blue-100'],
              borderColor: theme.palette['button-blue-050'],
              '&:hover': {
                color: theme.palette['button-blue-200'],
                backgroundColor: theme.palette['card-blue-000'],
              },
            },
          }),
        },

        {
          props: { variant: 'outlined', color: 'secondary' },
          style: ({ theme }) => ({
            '&.MuiButton-root': {
              color: theme.palette['button-green-200'],
              borderColor: theme.palette['button-green-100'],
              '&:hover': {
                backgroundColor: theme.palette['card-green-000'],
              },
            },
          }),
        },

        {
          props: { variant: 'outlined', color: 'error' },
          style: ({ theme }) => ({
            '&.MuiButton-root': {
              color: theme.palette['button-red-100'],
              borderColor: theme.palette['button-red-050'],
              '&:hover': {
                backgroundColor: theme.palette['card-red-000'],
              },
            },
          }),
        },

        {
          props: { variant: 'outlined', color: 'neutral' },
          style: ({ theme }) => ({
            '&.MuiButton-root': {
              color: theme.palette['button-grey-400'],
              borderColor: theme.palette['button-grey-300'],
              '&:hover': {
                backgroundColor: theme.palette['card-grey-100'],
              },
            },
          }),
        },

        {
          props: { variant: 'outlined', disabled: true },
          style: ({ theme }) => ({
            '&.Mui-disabled': {
              color: theme.palette['button-grey-300'],
              borderColor: theme.palette['button-grey-300'],
            },
          }),
        },

        {
          props: { variant: 'outlined', disabled: true, loading: true },
          style: ({ theme }) => ({
            '&.Mui-disabled': {
              color: theme.palette['button-grey-300'],
            },
          }),
        },

        // GROUP: TEXT
        {
          props: { variant: 'text', color: 'primary' },
          style: ({ theme }) => ({
            '&.MuiButton-root': {
              color: theme.palette['button-blue-100'],
              '&:hover': {
                color: theme.palette['button-blue-200'],
                backgroundColor: 'transparent',
              },
            },
          }),
        },

        {
          props: { variant: 'text', color: 'secondary' },
          style: ({ theme }) => ({
            '&.MuiButton-root': {
              color: theme.palette['button-green-100'],
              '&:hover': {
                color: theme.palette['button-green-200'],
                backgroundColor: 'transparent',
              },
            },
          }),
        },

        {
          props: { variant: 'text', color: 'error' },
          style: ({ theme }) => ({
            '&.MuiButton-root': {
              color: theme.palette['button-red-100'],
              borderColor: theme.palette['button-red-050'],
              '&:hover': {
                color: theme.palette['button-red-200'],
                backgroundColor: 'transparent',
              },
            },
          }),
        },

        {
          props: { variant: 'text', color: 'neutral' },
          style: ({ theme }) => ({
            '&.MuiButton-root': {
              color: theme.palette['button-grey-400'],
              borderColor: theme.palette['button-grey-300'],
              '&:hover': {
                color: theme.palette['button-grey-500'],
                backgroundColor: 'transparent',
              },
            },
          }),
        },

        {
          props: { variant: 'text', disabled: true },
          style: ({ theme }) => ({
            '&.Mui-disabled': {
              color: theme.palette['button-grey-300'],
            },
          }),
        },

        {
          props: { variant: 'text', disabled: true, loading: true },
          style: ({ theme }) => ({
            '&.Mui-disabled': {
              color: theme.palette['button-grey-300'],
              backgroundColor: 'transparent',
            },
          }),
        },

        // GROUP: SIZES
        {
          props: { size: 'small' },
          style: {
            minWidth: '44px',
            height: 'auto',
            fontSize: '12px',
            lineHeight: '16px',
            boxSizing: 'border-box',
            alignSelf: 'flex-start',
          },
        },
        {
          props: { size: 'medium' },
          style: {
            minWidth: '48px',
            boxSizing: 'border-box',
            alignSelf: 'flex-start',
          },
        },
        {
          props: { size: 'large' },
          style: {
            boxSizing: 'border-box',
            alignSelf: 'flex-start',
          },
        },
        {
          props: { size: 'extraLarge' },
          style: {
            boxSizing: 'border-box',
            alignSelf: 'flex-start',
          },
        },
        // Отступы для contained и text кнопок, для них одинаковые отступы,
        // потому что они не имеют border
        {
          props: (props) =>
            props.size === 'extraLarge' &&
            (props.variant === 'contained' || props.variant === 'text'),
          style: { padding: '14px 24px' },
        },
        {
          props: (props) =>
            props.size === 'large' &&
            (props.variant === 'contained' || props.variant === 'text'),
          style: { padding: '12px 24px' },
        },
        {
          props: (props) =>
            props.size === 'medium' &&
            (props.variant === 'contained' || props.variant === 'text'),
          style: { padding: '6px 16px' },
        },
        {
          props: (props) =>
            props.size === 'small' &&
            (props.variant === 'contained' || props.variant === 'text'),
          style: { padding: '4px 14px' },
        },

        // Outlined - по высоте на 1px больше чем contained,
        // так как у них есть border, поэтому уменьшаем padding на 1px
        {
          props: { size: 'extraLarge', variant: 'outlined' },
          style: { padding: '13px 24px' },
        },
        {
          props: { size: 'large', variant: 'outlined' },
          style: { padding: '11px 24px' },
        },
        {
          props: { size: 'medium', variant: 'outlined' },
          style: { padding: '5px 16px' },
        },
        {
          props: { size: 'small', variant: 'outlined' },
          style: { padding: '3px 14px' },
        },
      ],
      styleOverrides: {
        root: {
          fontSize: '16px',
          lineHeight: '22px',
          fontWeight: 500,

          textTransform: 'none',
          textWrap: 'nowrap',
          borderRadius: '4px',

          // Отключаем тени у кнопок
          boxShadow: 'none',
          '&:hover, &:active': {
            boxShadow: 'none',
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#24252D',
          borderRadius: '8px',
          padding: '0.5rem',
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        size: 'medium',
        fullWidth: true,
      },

      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-root': {
            padding: '0',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            transition: '0.15s',
            borderWidth: '1px!important',
            borderColor: theme.palette['line-grey-300'],
          },
          '& .MuiSelect-select span::before': {
            color: theme.palette['text-grey-350'],
          },
          '& .MuiInputBase-root': {
            fontSize: '1rem',
            lineHeight: 1.375,
            borderRadius: '4px',
            overflow: 'hidden',
            '&:not(.Mui-disabled)': {
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette['line-grey-400'],
                },
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette['line-blue-050'],
                },
              },
              '&.Mui-error': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette['line-red-050'],
                },
              },
            },

            '&.Mui-disabled': {
              '& .MuiInputBase-input::placeholder': {
                color: theme.palette['text-grey-300'],
                opacity: 1,
                WebkitTextFillColor: theme.palette['text-grey-300'],
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette['line-grey-150'],
              },
            },
          },

          '& .MuiInputBase-adornedEnd': {
            paddingRight: 0,
          },
          '& .MuiInputBase-input': {
            height: '22px',
            lineHeight: '22px',
            color: theme.palette['text-black'],

            '&:focus-visible': {
              outline: 'none',
            },
          },
        }),
      },

      variants: [
        {
          props: { size: 'small' },
          style: {
            '& .MuiInputBase-input': { padding: '6px 12px' },
            '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
              padding: '6px 12px',
            },
            '& .MuiAutocomplete-input': { padding: '6px 12px' },
          },
        },
        {
          props: { size: 'medium' },
          style: {
            '& .MuiInputBase-input': { padding: '12px 12px' },
            '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
              padding: '12px 12px',
            },
            '& .MuiAutocomplete-input': { padding: '12px 12px' },
          },
        },
      ],
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          lineHeight: 1.375,
          margin: '4px',
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: 'var(--ucr-palette-text-blue-100)',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },

    MuiBackdrop: {
      defaultProps: {},
    },

    MuiModal: {
      defaultProps: {},
    },
  },
});
